import { useContext } from "react";
import State from "../../../../../../context";
import dayjs from "dayjs";
import { Flex } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import ModalArchiveManagedUser from "../../../../../../molecule/ModalArchiveUser";
import ModalClientDetails from "../../component/ModalClientDetails";
import ModalClientPersonalInfo from "../../../admin/page/PagePeople/components/SectionDetailsClient/ModalClientPersonalInfo";
import ModalConvertIntoLead from "../../component/ModalConvertIntoLead";
import ModalImportManagedUser from "../../component/ModalImportManagedUser";
import SectionDetailsClient from "../../../admin/page/PagePeople/components/SectionDetailsClient";
import SectionHome from "./component/SectionHome";
import SecurityGate from "../../../../../../provider/SecurityGate";

import StylesContainer from "./style";

import {
  addManagedUser,
  archiveLead,
  importCSVManagedUserLead,
  markAsProspect,
  openManagedUser,
  openProposal,
  setLastAdvisorLogin,
  updateManagedUser,
} from "../../../../../../utils/request/manager";
import { openManagedUserGoals } from "../../../../../goals/request";
import {
  isTypeAffiliateAdvisor,
  isTypeProPortalAdvisor,
  isTypeProPortalAffiliateAdvisor,
  isPermissionReadonly,
} from "../../../super_admin/helper";
import { updateProductObjective } from "../../../../../../utils/request/regularApp";

import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";

const PageClients = () => {
  const [state] = useContext(State);

  const organization = state?.organization?.name;
  const userType = state.subscription?.userType;

  const handleActionArchiveClient = email => {
    state.setKeyValue("activeEmail", email);
    state.setKeyValue("archiveClientModal", true);
  };

  const handleActionEditClient = email => {
    state.setKeyValue("activeEmail", email);
    state.setKeyValue("openModalClientDetails", true);
  };

  const handleActionMoveClientToLead = email => {
    state.setKeyValue("activeEmail", email);
    state.setKeyValue("openModalConvertIntoLead", true);
  };

  const handleClientNameClick = clientEmail => {
    state.setKeyValue("activeEmail", clientEmail);
    state.setKeyValue("activeSection", "client");
  };

  const handleExportClients = () => {
    window.open(
      `${process.env.REACT_APP_API_GATEWAY}/userManager/export?email=${encodeURIComponent(state._id)}&userType=Client`,
      "_blank"
    );
  };

  const handleImportClientsClick = () => {
    state.setKeyValue("importManagedUserModal", true);
  };

  const handleNewClientsModalOpen = () => {
    state.setKeyValue("activeEmail", null);
    state.setKeyValue("openModalClientDetails", true);
  };

  const onClickEditInfo = editClientInfoViewType => {
    state.setKeyValue("editClientInfoViewType", editClientInfoViewType);
    state.setKeyValue("openModalClientPersonalInfo", true);
  };

  const onClickProposalCreate = clientEmail => {
    const proposalData = state?.managedUsers?.find(
      it => it.email === clientEmail
    );

    const requestBody = {
      email: clientEmail,
      contributions: 12000,
      riskTolerance: "Moderate",
      investmentObjective: "growth",
      investmentAmount: 100000,
      time: 25,
      organization: "level",
    };

    updateProductObjective({
      requestBody,
      userEmail: state._id,
    })
      .then(() =>
        openProposal({
          clientEmail,
          managerEmail: state._id,
          organization,
          showQuestionnaire: !proposalData?.valueMap?.productMap,
        }).catch(state.showError)
      )
      .catch(state.showError);
  };

  const onClickProposalOpen = clientEmail => {
    if (
      isTypeProPortalAdvisor(userType) ||
      isTypeProPortalAffiliateAdvisor(userType)
    ) {
      openManagedUser({
        clientEmail,
        managerEmail: state._id,
      });
    } else if (isTypeAffiliateAdvisor(userType)) {
      openManagedUserGoals({
        clientEmail,
        managerEmail: state._id,
      });
    } else {
      openProposal({
        advisorEmail: state._id,
        clientEmail,
        organization: state.orgName ?? state.organization?.name,
      });
    }

    setLastAdvisorLogin({
      advisorEmail: state._id,
      clientEmail,
    }).then(() => state.setUserData());
  };

  const submitArchiveClient = () => {
    state.setKeyValue("archiveClientModal", false);
    state.setKeyValue("loading", true);

    archiveLead({
      email: state.activeEmail,
      userManagerEmail: state._id,
    })
      .then(() =>
        state.setUserData().then(() => {
          state.setKeyValue("loading", false);
          state.setKeyValue("activeEmail", null);
          state.showSuccess(`Client ${state.activeEmail} archived`);
        })
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const submitClientDetails = values => {
    if (
      values.birthYear &&
      values.startDate &&
      values.birthYear > dayjs(values.startDate?.$d).format("YYYY")
    ) {
      state.showWarning("Birth Year could not be bigger, than Start Date.");
      return;
    }

    state.setKeyValue("loading", true);

    // edit active user
    if (state.activeEmail) {
      const clientData = state.managedUsers.find(
        it => it.email === state.activeEmail
      );

      updateManagedUser({
        managerEmail: state._id,
        requestPayload: {
          ...clientData,
          ...values,
        },
      })
        .then(() => {
          state.setUserData().then(() => {
            state.setKeyValue("openModalClientDetails", false);
            state.showSuccess("Client data updated");
          });
        })
        .catch(state.showError);
      return;
    }

    // create new user
    const requestBody = {
      assets: values.assets,
      birthYear: values.birthYear,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      phoneNumber: "", // required to be passed
      retirementAge: values.retirementAge,
      scenarioName: "Scenario 1", // required to be passed
      startDate: new Date(),
      userManagerEmail: state._id,
    };

    addManagedUser({ managerEmail: state._id, requestBody })
      .then(() =>
        state.setUserData().then(() => {
          state.showSuccess(`New Client "${values.email}" Successfully Added`);
          state.setKeyValue("openModalClientDetails", false);

          values.startPlaning && onClickProposalCreate(values.email);
        })
      )
      .catch(error => {
        state.setKeyValue("loading", false);
        state.showError(error);
      });
  };

  const submitConvertIntoLead = () => {
    state.setKeyValue("loading", true);
    state.setKeyValue("openModalConvertIntoLead", false);

    markAsProspect({ clientEmail: state.activeEmail, managerEmail: state._id })
      .then(() => {
        state.showSuccess("Client converted into Lead!");
        state.setUserData();
      })
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const submitClientPersonalInfo = values => {
    state.setKeyValue("loading", true);

    updateManagedUser({
      managerEmail: state._id,
      requestPayload: values,
    })
      .then(() =>
        state
          .setUserData()
          .then(() => {
            values.email && state.setKeyValue("activeEmail", values.email);
            state.setKeyValue("openModalClientPersonalInfo", false);
            state.showSuccess("Personal data updated");
          })
          .catch(state.showError)
          .finally(() => state.setKeyValue("loading", false))
      )
      .catch(error => {
        state.showError(error);
        state.setKeyValue("loading", false);
      });
  };

  const submitImportClients = file => {
    importCSVManagedUserLead({ file, managerEmail: state._id })
      .then(response => {
        if (typeof response?.data === "object") {
          Object.keys(response.data).map(key =>
            state.showWarning(`${key} ${response.data[key]}`)
          );
        }

        state.setUserData().then(() => {
          state.setKeyValue("loading", false);
          state.setKeyValue("importManagedUserModal", false);
          state.showSuccess("Clients list imported.");
        });
      })
      .catch(error => {
        state.setKeyValue("loading", false);
        state.showError(error);
      });
  };

  return (
    <SecurityGate state={state}>
      <StylesContainer>
        {state.activeSection === "client" && (
          <SectionDetailsClient
            clientData={state.managedUsers.find(
              it => it.email === state.activeEmail
            )}
            disableEdit={isPermissionReadonly(state.permissions?.manageClients)}
            itemsBreadcrumb={[
              {
                onClick: () => state.setKeyValue("activeSection", "home"),
                title: (
                  <Flex align="center" gap={8} style={{ cursor: "pointer" }}>
                    <FontAwesomeIcon
                      icon={faArrowLeft}
                      style={{ fontSize: 14 }}
                    />
                    <b>All Users</b>
                  </Flex>
                ),
              },
              {
                title: <span>User Profile</span>,
              },
            ]}
            managerEmail={state._id}
            onClickEditInfo={onClickEditInfo}
            onClickProposalOpen={onClickProposalOpen}
            organization={state.organization?.name}
            productsList={state.productsList}
          />
        )}

        {state.activeSection !== "client" && (
          <SectionHome
            listClients={state.managedUsers?.filter(
              it =>
                it?.valueMap?.leadInitial?.isConverted ||
                it.userType === "Registered"
            )}
            loading={state.loading}
            permissions={state.permissions}
            handleActionArchiveClient={handleActionArchiveClient}
            handleActionEditClient={handleActionEditClient}
            handleActionMoveClientToLead={handleActionMoveClientToLead}
            handleClientNameClick={handleClientNameClick}
            handleExportClients={handleExportClients}
            handleImportClientsClick={handleImportClientsClick}
            handleNewClientsModalOpen={handleNewClientsModalOpen}
            onClickProposalOpen={onClickProposalOpen}
            onClickProposalCreate={onClickProposalCreate}
            userType={userType}
          />
        )}

        <ModalArchiveManagedUser
          clientData={state.managedUsers?.find(
            it => it.email === state.activeEmail
          )}
          loading={state.loading}
          onCancel={() => state.setKeyValue("archiveClientModal", false)}
          onSuccess={submitArchiveClient}
          open={state.archiveClientModal}
        />
        <ModalClientDetails
          clientData={state.managedUsers?.find(
            it => it.email === state.activeEmail
          )}
          onClickProposalOpen={onClickProposalOpen}
          loading={state.loading}
          onCancel={() => state.setKeyValue("openModalClientDetails", false)}
          onFinish={submitClientDetails}
          open={state.openModalClientDetails}
        />
        <ModalClientPersonalInfo
          clientData={state.managedUsers?.find(
            it => it.email === state.activeEmail
          )}
          loading={state.loading}
          onCancel={() =>
            state.setKeyValue("openModalClientPersonalInfo", false)
          }
          onFinish={submitClientPersonalInfo}
          open={state.openModalClientPersonalInfo}
          viewType={state.editClientInfoViewType}
        />
        <ModalConvertIntoLead
          email={state.activeEmail}
          onCancel={() => state.setKeyValue("openModalConvertIntoLead", false)}
          onSuccess={submitConvertIntoLead}
          open={state.openModalConvertIntoLead}
        />
        <ModalImportManagedUser
          instanceLabel="client"
          loading={state.loading}
          managerEmail={state._id}
          onCancel={() => state.setKeyValue("importManagedUserModal", false)}
          onFinish={submitImportClients}
          open={state.importManagedUserModal}
        />
      </StylesContainer>
    </SecurityGate>
  );
};

export default PageClients;
